// The timeline's time axis: the window a zoom level covers, the slots it is divided
// into, and where an item sits inside it.
//
// Day, week, month, quarter, and year are not five different charts — they are one
// horizontal axis with a different window and a different slot size. All of this is
// pure and dateless-by-argument (the anchor is passed in), so the views hold no
// time logic at all.

import type { TimelineItem } from "./items";

export const ZOOMS = ["list", "day", "week", "month", "quarter", "year"] as const;
export type Zoom = (typeof ZOOMS)[number];

export const ZOOM_LABEL: Record<Zoom, string> = {
  list: "List",
  day: "Day",
  week: "Week",
  month: "Month",
  quarter: "Quarter",
  year: "Year",
};

export type Range = { startMs: number; endMs: number };
export type Slot = { key: number; label: string; leftPct: number; widthPct: number };
export type Bar = { leftPct: number; widthPct: number };

const PCT_MAX = 100;
/** An instant (an event, a reading) has no width — give it enough to be clickable. */
const MIN_BAR_PCT = 0.8;
/** Slot size for the day view: 30 minutes, labelled on the hour. */
const DAY_SLOT_MINUTES = 30;
const MONTHS_PER_QUARTER = 3;

/** The window a zoom level shows around its anchor. */
export function rangeFor(zoom: Zoom, anchor: Date): Range {
  const start = new Date(anchor);
  start.setHours(0, 0, 0, 0);

  switch (zoom) {
    case "day":
      return span(start, addDays(start, 1));
    case "week": {
      const weekStart = startOfWeek(start);
      return span(weekStart, addDays(weekStart, 7));
    }
    case "month": {
      const monthStart = startOfMonth(start);
      return span(monthStart, addMonths(monthStart, 1));
    }
    case "quarter": {
      const quarterStart = startOfQuarter(start);
      return span(quarterStart, addMonths(quarterStart, MONTHS_PER_QUARTER));
    }
    case "year":
    case "list": {
      const yearStart = new Date(start.getFullYear(), 0, 1);
      return span(yearStart, new Date(start.getFullYear() + 1, 0, 1));
    }
  }
}

/** Step the anchor one window forward (+1) or back (-1). */
export function shiftAnchor(zoom: Zoom, anchor: Date, delta: number): Date {
  switch (zoom) {
    case "day":
      return addDays(anchor, delta);
    case "week":
      return addDays(anchor, delta * 7);
    case "month":
      return addMonths(anchor, delta);
    case "quarter":
      return addMonths(anchor, delta * MONTHS_PER_QUARTER);
    case "year":
    case "list":
      return addMonths(anchor, delta * 12);
  }
}

/** The window's name, e.g. "Q3 2026" or "Sun, Jul 12". */
export function titleFor(zoom: Zoom, anchor: Date): string {
  switch (zoom) {
    case "day":
      return anchor.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    case "week": {
      const from = startOfWeek(anchor);
      const to = addDays(from, 6);
      return `${shortDay(from)} – ${shortDay(to)}, ${to.getFullYear()}`;
    }
    case "month":
      return anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    case "quarter": {
      const quarter = Math.floor(anchor.getMonth() / MONTHS_PER_QUARTER) + 1;
      return `Q${quarter} ${anchor.getFullYear()}`;
    }
    case "year":
    case "list":
      return String(anchor.getFullYear());
  }
}

/** The window divided into its slots: half-hours, days, weeks, or months. */
export function slotsFor(zoom: Zoom, range: Range): Slot[] {
  switch (zoom) {
    case "day":
      return minuteSlots(range, DAY_SLOT_MINUTES);
    case "week":
      return daySlots(range, (date) => shortDay(date));
    case "month":
      return daySlots(range, (date) => String(date.getDate()));
    case "quarter":
      return weekSlots(range);
    case "year":
      return monthSlots(range);
    case "list":
      return [];
  }
}

/** Where an item sits on the axis, or null when it falls outside the window. */
export function barFor(range: Range, item: TimelineItem): Bar | null {
  if (item.endMs < range.startMs || item.startMs > range.endMs) return null;

  const leftPct = clamp(pctOf(range, item.startMs));
  const widthPct = Math.max(MIN_BAR_PCT, clamp(pctOf(range, item.endMs)) - leftPct);
  return { leftPct, widthPct: Math.min(widthPct, PCT_MAX - leftPct) };
}

/** Where "now" sits, or null when the window is not the present one. */
export function nowPct(range: Range, now: number): number | null {
  if (now < range.startMs || now > range.endMs) return null;
  return pctOf(range, now);
}

/**
 * Stack items into lanes so that no two overlapping items share a row. Greedy by
 * start time: an item takes the first lane whose last item has already ended.
 */
export function packLanes(items: TimelineItem[], range: Range): TimelineItem[][] {
  const lanes: TimelineItem[][] = [];
  const laneEnd: number[] = [];

  for (const item of items) {
    if (item.endMs < range.startMs || item.startMs > range.endMs) continue;
    const lane = laneEnd.findIndex((end) => end <= item.startMs);
    if (lane === -1) {
      lanes.push([item]);
      laneEnd.push(item.endMs);
    } else {
      (lanes[lane] as TimelineItem[]).push(item);
      laneEnd[lane] = item.endMs;
    }
  }
  return lanes;
}

/** Items inside the window, oldest first — the list view's rows. */
export function itemsIn(items: TimelineItem[], range: Range): TimelineItem[] {
  return items.filter((item) => item.endMs >= range.startMs && item.startMs <= range.endMs);
}

// --- Slot builders ---

function minuteSlots(range: Range, minutes: number): Slot[] {
  const step = minutes * 60_000;
  const slots: Slot[] = [];
  for (let at = range.startMs; at < range.endMs; at += step) {
    const date = new Date(at);
    // Label on the hour only — a label every half hour is unreadable at this width.
    const onTheHour = date.getMinutes() === 0;
    slots.push({
      key: at,
      label: onTheHour ? String(date.getHours()).padStart(2, "0") : "",
      leftPct: pctOf(range, at),
      widthPct: (step / (range.endMs - range.startMs)) * PCT_MAX,
    });
  }
  return slots;
}

function daySlots(range: Range, label: (date: Date) => string): Slot[] {
  const slots: Slot[] = [];
  for (let cursor = new Date(range.startMs); cursor.getTime() < range.endMs; ) {
    const next = addDays(cursor, 1);
    slots.push(slot(range, cursor, next, label(cursor)));
    cursor = next;
  }
  return slots;
}

function weekSlots(range: Range): Slot[] {
  const slots: Slot[] = [];
  for (let cursor = startOfWeek(new Date(range.startMs)); cursor.getTime() < range.endMs; ) {
    const next = addDays(cursor, 7);
    slots.push(slot(range, cursor, next, shortDay(cursor)));
    cursor = next;
  }
  return slots;
}

function monthSlots(range: Range): Slot[] {
  const slots: Slot[] = [];
  for (let cursor = new Date(range.startMs); cursor.getTime() < range.endMs; ) {
    const next = addMonths(cursor, 1);
    const label = cursor.toLocaleDateString(undefined, { month: "short" });
    slots.push(slot(range, cursor, next, label));
    cursor = next;
  }
  return slots;
}

function slot(range: Range, from: Date, to: Date, label: string): Slot {
  const leftPct = pctOf(range, from.getTime());
  return {
    key: from.getTime(),
    label,
    leftPct,
    widthPct: clamp(pctOf(range, to.getTime())) - leftPct,
  };
}

// --- Date helpers ---

function span(from: Date, to: Date): Range {
  return { startMs: from.getTime(), endMs: to.getTime() };
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

/** Weeks start on Monday. */
function startOfWeek(date: Date): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const weekday = (start.getDay() + 6) % 7;
  return addDays(start, -weekday);
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function startOfQuarter(date: Date): Date {
  const month = Math.floor(date.getMonth() / MONTHS_PER_QUARTER) * MONTHS_PER_QUARTER;
  return new Date(date.getFullYear(), month, 1);
}

function shortDay(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function pctOf(range: Range, at: number): number {
  return ((at - range.startMs) / (range.endMs - range.startMs)) * PCT_MAX;
}

function clamp(value: number): number {
  return Math.min(PCT_MAX, Math.max(0, value));
}
